<template>
  <div :class="['export-profile-item', hasInstancesInCurrentProject ? 'active' : 'inactive', highlight ? 'highlighted' : '']">
    <popup
        v-if="showDeletePrompt"
        @close="() => {showDeletePrompt = false;}"
    >
      <delete-prompt
          slot="popupMainContent"
          @abort="deleteAbort"
          @confirm="deleteInstance"
      />
    </popup>
    <p
        v-if="profile.updatedAt"
        class="float-right">
    <span class="lighter">
      {{ $t('UpdatedAt') }}</span>
      {{ dateTimeFromISO(profile.updatedAt) }}
    </p>

    <div class="profile-title d-flex align-items-center">
      <name-display-edit-block
          v-if="profile.name !== 'new'"
          :id="profile.id"
          :display-name="profile.displayName"
          :name="profile.name"
          :organization-id="organizationId"
          :edit-icon-size="0.8"
          html-tag-for-display="p"
          :icon="profile.sourceProjectId != projectId ? 'globe' : 'filter'"
          :editable="!showEditForm"
          store-name="Filter"
          @updated="$emit('reloadItem')"
          @editing="(val) => {nameEditing = val;}"
      />
    </div>
    <template v-if="!isNew && !showEditForm && !showStartForm && !nameEditing">
      <p v-if="profile.description" class="lighter">{{ profile.description }}</p>
      <div class="w-100"/>
      <div class="vform-label white">{{ $t('teams') }}</div>
      <div v-if="!localTeams.length">
        -
      </div>
      <div
          class="tag mr-1 mb-1"
          :key="team.id"
          v-for="team in localTeams">
        {{ team.displayName }}
      </div>
      <div class="vform-label white">{{ $t('tags') }}</div>
      <div v-if="!localTags.length">
        -
      </div>
      <tag
          v-for="tag in localTags"
          :key="tag.id"
          :tag="tag"
          :is-removable="false"
          class="mr-1 mb-1"
      />
      <hr v-if="!showEditForm" />
      <div
          v-if="!showEditForm"
          class="settings-button d-inline-block switched-colors mt-2 mr-2"
          @click="showEditForm = true">
        {{ $t('edit') }}
        <icon type="pencil-alt"/>
      </div>
      <div
          v-if="!showEditForm"
          @click="showStartForm = true"
          class="settings-button d-inline-block switched-colors mt-2 mr-2">
        {{ $t('start') }}
        <icon type="angle-right"/>
      </div>
      <hr/>
    </template>
    <!--EDIT -->
    <div class="profile-details" v-if="!nameEditing">
      <div v-if="showStartForm" class="pr-3 pb-3">
        <filter-trigger-form
            v-if="projectId && rootNode"
            :filter-item="profile"
            :instance-id="null"
            :project-id="projectId"
            :root-node="rootNode.id"
            :input-units="inputUnits"
            @abort="showStartForm = false"
            @started="started"
        />
      </div>
      <div v-else-if="showEditForm" class="pr-3 pb-3">
        <filter-add-form
            :assembly-id="assemblyId"
            :filter-id="profile.id"
            :is-editing="true"
            :new-filter="isNew"
            :organization-id="organizationId"
            :project-id="projectId"
            :tags="localTags"
            :teams="localTeams"
            @aborted="aborted"
            @created="created"
            @deleted="deleted"
            @updated="updated"
            @loadAssetTags="loadAssetTags"
            @loadAssetTeams="loadAssetTeams"
            @showPreview="$emit('showPreview', profile.id)"
        />
      </div>
      <div v-else-if="!hasInstancesInCurrentProject" class="lighter">
        {{ $t('filter.noExecutedItems') }}
      </div>
      <div v-else>
        <h4 class="lighter">{{ $t('filter.executedItems') }}</h4>
        <table>
          <!--Just insert two test rows for now with: name, date, linked in projects (5), Download and alert, those horizontal, not vertical, so all info for those 2 items is on the same line-->
          <tr>
            <th class="image-header">{{ $t('Image') }}</th>
            <th class="name">{{ $t('Name') }}</th>
            <th>{{ $t('Used in projects') }}</th>
            <th class="download-header">{{ $t('Download') }}</th>
            <th>{{ $t('Sync') }}</th>
            <th>{{ $t('State') }}</th>
          </tr>
          <export-profile-instance
              v-for="instance in profile.instances"
              v-if="instance.projectId === projectId"
              :key="instance.id"
              :instance="instance"
              :parent-content="profile.content"
              :linked-export-profiles="linkedExportProfiles"
              @deleteInstance="deleteInstance(instance.id)"
              @updated="$emit('instanceUpdated', instance.id)"
          />
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/Icon'
import ExportProfileInstance from "@/components/exportProfiles/ExportProfileInstance.vue";
import DateMixin from "@/components/mixins/DateMixin.js.vue";
import FilterAddForm from "@/components/sfxFilter/FilterAddForm.vue";
import FilterTriggerForm from "@/components/sfxFilter/FilterTriggerForm.vue";
import Tag from "@/components/Tag.vue";
import DeletePrompt from "@/components/forms/DeletePrompt.vue";
import Popup from "@/components/Popup.vue";
import {sleep} from "@/utils";
import NameDisplayEditBlock from "@/components/NameDisplayEditBlock.vue";
export default {
  name: "ExportProfileItem",
  components: {
    NameDisplayEditBlock,
    Popup, DeletePrompt, Icon, ExportProfileInstance, FilterAddForm, FilterTriggerForm, Tag},
  mixins: [DateMixin],
  props: {
    profile: {type: Object, required: true},
    organizationId: {type: String, required: true},
    projectId: {type: String, required: true},
    assemblyId: {type: String, required: true},
    rootNode: {type: Object, required: true},
    inputUnits: {type: String, required: true},
    linkedExportProfiles: {type: Array, required: false, default: () => []},
    highlight: {type: Boolean, required: false, default: false},
  },
  data() {
    return {
      forceReRenderKey: 0,
      showEditForm: false,
      editName: false,
      localName: '',
      showStartForm: false,
      localTags: [],
      localTeams: [],
      showDeletePrompt: false,
      toBeDeletedInstance: null,
      nameEditing: false
    };
  },
  computed: {
    hasInstancesInCurrentProject() {
      return this.profile.instances && this.profile.instances.filter(instance => instance.projectId === this.projectId).length > 0;
    },
    isNew() {
      return this.profile.id === 'new';
    }
  },
  watch: {
    showEditForm() {
      this.$emit('currentlyEditing', this.showEditForm ? this.profile.id : null);
    },
    showStartForm() {
      this.$emit('currentlyEditing', this.showStartForm ? this.profile.id : null);
    },
    profile: {
      handler() {
        console.log('profile changed, reloading tags and teams');
      },
      deep: true
    }
  },
  beforeMount() {
    if (this.isNew) {
      this.showEditForm = true;
    }
    if(this.profile && this.profile.tags) {
      this.localTags = this.profile.tags;
    }
    if(this.profile && this.profile.teams) {
      this.localTeams = this.profile.teams;
    }
    this.sortInstancesByDate();
  },
  methods: {
    started() {
      // 1 set currentlyEditing to null
      this.showStartForm = false;
      this.$emit('currentlyEditing', null);
      this.$emit('reloadItem', {id: this.profile.id});
    },
    deleteAbort() {
      this.showDeletePrompt = false;
      this.toBeDeletedInstance = null;
    },
    async deleteInstance(cid = null) {
      if (this.toBeDeletedInstance) {
        return await this.$store.dispatch('clientDeleteProjectInstance', {
          id: this.projectId,
          cid: this.toBeDeletedInstance
        }).then(async () => {
          this.$emit('instanceDeleted', [this.toBeDeletedInstance]);
          await sleep(600);
          this.forceReRenderKey++;
          this.showDeletePrompt = false;
          this.toBeDeletedInstance = null;
          this.$emit('deleted');
        }).catch(e => {
          console.log(e);
          this.$store.dispatch('createNotification', {text: this.$t('events.instanceDeleteFailed')});
          this.deleteAbort()
        });
      } else {
        this.toBeDeletedInstance = cid;
        this.showDeletePrompt = true;
      }
    },
    sortInstancesByDate() {
      if (this.profile.instances && this.profile.instances.length) {
        this.profile.instances.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      }
    },
    startEditingName() {
      this.localName = this.profile.name;
      this.editName = true;
    },
    revertName() {
      console.log('reverting name edit');
      this.editName = false;
      this.localName = '';
    },
    saveNewName() {
      console.log('what are you doing??')
      console.log('saving name:', this.localName);
      this.$store.dispatch('updateFilter', {
        id: this.profile.id,
        name: this.localName,
        displayName: this.localName
      }).then(() => {
        this.$store.dispatch('createNotification', {
          type: 'success',
          message: this.$t('filter.nameUpdatedSuccessfully')
        });
        this.editName = false;
        this.reloadItem();
      }).catch(err => {
        console.error('error saving name:', err);
      });
    },
    cancelEditing() {
      console.log('cancelling editing');
      this.$store.dispatch('loadFilter', {
        id: this.profile.id,
      })
      this.showEditForm = false;
    },
    async reloadItem(args) {
      console.log('reload item', args);
      this.$emit('reloadItem', {...args, id: this.profile.id});
    },
    deleted() {
      const instanceIds = this.profile.instances ? this.profile.instances.map(i => i.id) : [];
      if(instanceIds.length) {
        this.$emit('instanceDeleted', instanceIds);
      }
    },
    updated(args) {
      console.log('updated', args);
      this.showEditForm = false;
      this.reloadItem(args);
    },
    created(asset) {
      console.log('created');
      this.showEditForm = false;
      this.$emit('created', asset);
    },
    aborted() {
      this.showEditForm = false;
      this.$emit('aborted');
    },
    loadAssetTags() {
      console.log('loading asset tags')
      this.$store.dispatch('clientLoadAssetTags', {
        id: this.profile.id
      }).then(data => {
        this.localTags = data;
      })
    },
    loadAssetTeams() {
      this.$store.dispatch('clientLoadAssetTeams', {
        id: this.profile.id
      }).then(data => {
        this.localTeams = data;
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.export-profile-item {
  background-color: var(--vform-editor-ui-secondary-color);
  padding: 10px;
  border-radius: 5px;

  &.active {
    border: 1px solid rgba(255,255,255,0.3);
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.2);
  }
  &.highlighted {
    border: 1px solid $highlight;
  }
}

hr {
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin: 10px 0;
  width: 100%;
}

.profile-title {
  font-size: 1.2rem;
}

.profile-details {
  transition: all 300ms ease;
}

.edit-button {
  cursor: pointer;
  background-color: var(--vform-editor-ui-quinary-color);
  border-radius: 3px;
  padding: 2px 6px;
  transition: background-color 300ms ease;

  &:hover {
    background-color: var(--vform-editor-ui-tertiary-color);
  }
}
.image-header {
  width: 60px;
}
.download-header {
  width: 90px;
}
.name {
  min-width: 250px;
}
</style>